package com.bulut.mecitogullari.core.i18n

import androidx.compose.runtime.staticCompositionLocalOf
import com.bulut.mecitogullari.core.storage.AppStorage
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/** Product brand — never translated, never tenant-specific. */
const val APP_NAME = "YAPEX"

enum class AppLanguage(val code: String, val displayName: String) {
    TURKISH("tr", "Türkçe"),
    ENGLISH("en", "English"),
    SPANISH("es", "Español");

    companion object {
        fun fromCode(code: String?): AppLanguage? {
            val normalized = code?.lowercase()?.substringBefore('-') ?: return null
            return entries.firstOrNull { it.code == normalized }
        }
    }
}

/**
 * Every user-facing string of the app, per language (see StringsTr/En/Es).
 * Screens read these via [LocalStrings]; view models, formatters and the
 * printable reports use [Localization.strings].
 *
 * The company name shown in the app and on report letterheads is the user's
 * own (Profil -> Firma Adi) - strings here must stay tenant-neutral.
 *
 * Backed by a map rather than constructor parameters: a constructor with one
 * argument per key overflows the 255-register limit of an Android DEX
 * invocation (java.lang.VerifyError at class init). Property access below is
 * unchanged for callers.
 *
 * Templates use {placeholders}; fill them with [fillTemplate].
 */
class Strings(
    val monthNames: List<String>,
    val monthNamesShort: List<String>,
    /** Stored (Turkish) category value -> localized display label. */
    val categoryLabels: Map<String, String>,
    private val values: Map<String, String>,
) {
    val income: String get() = values.getValue("income")
    val expense: String get() = values.getValue("expense")
    val netProfit: String get() = values.getValue("netProfit")
    val total: String get() = values.getValue("total")
    val date: String get() = values.getValue("date")
    val description: String get() = values.getValue("description")
    val category: String get() = values.getValue("category")
    val project: String get() = values.getValue("project")
    val amount: String get() = values.getValue("amount")
    val cancel: String get() = values.getValue("cancel")
    val save: String get() = values.getValue("save")
    val add: String get() = values.getValue("add")
    val delete: String get() = values.getValue("delete")
    val back: String get() = values.getValue("back")
    val ok: String get() = values.getValue("ok")
    val savedCheck: String get() = values.getValue("savedCheck")
    val phoneOptional: String get() = values.getValue("phoneOptional")
    val noteOptional: String get() = values.getValue("noteOptional")
    val none: String get() = values.getValue("none")
    val loginTagline: String get() = values.getValue("loginTagline")
    val loginHighlights: String get() = values.getValue("loginHighlights")
    val continueWithGoogle: String get() = values.getValue("continueWithGoogle")
    val continueAsGuest: String get() = values.getValue("continueAsGuest")
    val loginFooter: String get() = values.getValue("loginFooter")
    val errorGoogleToken: String get() = values.getValue("errorGoogleToken")
    val errorSignInFailed: String get() = values.getValue("errorSignInFailed")
    val errorGuestFailed: String get() = values.getValue("errorGuestFailed")
    val guestName: String get() = values.getValue("guestName")
    val defaultUserName: String get() = values.getValue("defaultUserName")
    val tabHome: String get() = values.getValue("tabHome")
    val tabTransactions: String get() = values.getValue("tabTransactions")
    val tabProjects: String get() = values.getValue("tabProjects")
    val tabReports: String get() = values.getValue("tabReports")
    val tabProfile: String get() = values.getValue("tabProfile")
    val greeting: String get() = values.getValue("greeting")
    val cashSummaryTemplate: String get() = values.getValue("cashSummaryTemplate")
    val netProfitTitle: String get() = values.getValue("netProfitTitle")
    val closingBalanceLabel: String get() = values.getValue("closingBalanceLabel")
    val statCarryOver: String get() = values.getValue("statCarryOver")
    val statMonthIncome: String get() = values.getValue("statMonthIncome")
    val statMonthExpense: String get() = values.getValue("statMonthExpense")
    val recentTransactions: String get() = values.getValue("recentTransactions")
    val seeAll: String get() = values.getValue("seeAll")
    val dashboardEmpty: String get() = values.getValue("dashboardEmpty")
    val addTransaction: String get() = values.getValue("addTransaction")
    val transactionsSearchHint: String get() = values.getValue("transactionsSearchHint")
    val tabAll: String get() = values.getValue("tabAll")
    val noRecordsForMonth: String get() = values.getValue("noRecordsForMonth")
    val noSearchMatch: String get() = values.getValue("noSearchMatch")
    val shortcutDebtSubtitle: String get() = values.getValue("shortcutDebtSubtitle")
    val shortcutCraftsmenSubtitle: String get() = values.getValue("shortcutCraftsmenSubtitle")
    val excelMenu: String get() = values.getValue("excelMenu")
    val exportExcel: String get() = values.getValue("exportExcel")
    val importExcel: String get() = values.getValue("importExcel")
    val importExcelToProject: String get() = values.getValue("importExcelToProject")
    val downloadMonthPdf: String get() = values.getValue("downloadMonthPdf")
    val downloadPdf: String get() = values.getValue("downloadPdf")
    val previousMonth: String get() = values.getValue("previousMonth")
    val nextMonth: String get() = values.getValue("nextMonth")
    val profileTitle: String get() = values.getValue("profileTitle")
    val statProjects: String get() = values.getValue("statProjects")
    val statSoldUnits: String get() = values.getValue("statSoldUnits")
    val statMonthNet: String get() = values.getValue("statMonthNet")
    val companySection: String get() = values.getValue("companySection")
    val companyNameLabel: String get() = values.getValue("companyNameLabel")
    val shortcutsSection: String get() = values.getValue("shortcutsSection")
    val debtTracking: String get() = values.getValue("debtTracking")
    val debtTrackingSubtitle: String get() = values.getValue("debtTrackingSubtitle")
    val craftsmenTitle: String get() = values.getValue("craftsmenTitle")
    val craftsmenSubtitle: String get() = values.getValue("craftsmenSubtitle")
    val languageSection: String get() = values.getValue("languageSection")
    val logout: String get() = values.getValue("logout")
    val projectsEmpty: String get() = values.getValue("projectsEmpty")
    val newProject: String get() = values.getValue("newProject")
    val statSales: String get() = values.getValue("statSales")
    val statNetIncome: String get() = values.getValue("statNetIncome")
    val reportsTitle: String get() = values.getValue("reportsTitle")
    val expenseBreakdown: String get() = values.getValue("expenseBreakdown")
    val incomeBreakdown: String get() = values.getValue("incomeBreakdown")
    val noExpenseThisMonth: String get() = values.getValue("noExpenseThisMonth")
    val noIncomeThisMonth: String get() = values.getValue("noIncomeThisMonth")
    val cashNoteSection: String get() = values.getValue("cashNoteSection")
    val cashNotePlaceholder: String get() = values.getValue("cashNotePlaceholder")
    val saveNote: String get() = values.getValue("saveNote")
    val carryToNextMonth: String get() = values.getValue("carryToNextMonth")
    val openingBalanceLabel: String get() = values.getValue("openingBalanceLabel")
    val debtLabel: String get() = values.getValue("debtLabel")
    val receivableLabel: String get() = values.getValue("receivableLabel")
    val closedLabel: String get() = values.getValue("closedLabel")
    val addCustomer: String get() = values.getValue("addCustomer")
    val addSupplier: String get() = values.getValue("addSupplier")
    val debtsEmpty: String get() = values.getValue("debtsEmpty")
    val customer: String get() = values.getValue("customer")
    val supplier: String get() = values.getValue("supplier")
    val newCustomer: String get() = values.getValue("newCustomer")
    val newSupplier: String get() = values.getValue("newSupplier")
    val customerAccountSubtitle: String get() = values.getValue("customerAccountSubtitle")
    val supplierAccountSubtitle: String get() = values.getValue("supplierAccountSubtitle")
    val customerNameLabel: String get() = values.getValue("customerNameLabel")
    val firmNameLabel: String get() = values.getValue("firmNameLabel")
    val addCraftsman: String get() = values.getValue("addCraftsman")
    val craftsmenEmpty: String get() = values.getValue("craftsmenEmpty")
    val craftsmanOwed: String get() = values.getValue("craftsmanOwed")
    val craftsmanOwes: String get() = values.getValue("craftsmanOwes")
    val balanced: String get() = values.getValue("balanced")
    val workDone: String get() = values.getValue("workDone")
    val unitsGivenCount: String get() = values.getValue("unitsGivenCount")
    val craftsman: String get() = values.getValue("craftsman")
    val invoiceProgress: String get() = values.getValue("invoiceProgress")
    val units: String get() = values.getValue("units")
    val newCraftsman: String get() = values.getValue("newCraftsman")
    val newCraftsmanIntro: String get() = values.getValue("newCraftsmanIntro")
    val craftsmanNameLabel: String get() = values.getValue("craftsmanNameLabel")
    val tradeLabel: String get() = values.getValue("tradeLabel")
    val agreedWorkSection: String get() = values.getValue("agreedWorkSection")
    val countLabel: String get() = values.getValue("countLabel")
    val pricePerUnitLabel: String get() = values.getValue("pricePerUnitLabel")
    val requiredInvoiceLabel: String get() = values.getValue("requiredInvoiceLabel")
    val requiredInvoiceHint: String get() = values.getValue("requiredInvoiceHint")
    val pdfMonthlyReport: String get() = values.getValue("pdfMonthlyReport")
    val pdfMonthlyReportSubtitle: String get() = values.getValue("pdfMonthlyReportSubtitle")
    val pdfTransactionCount: String get() = values.getValue("pdfTransactionCount")
    val pdfCarriedBalance: String get() = values.getValue("pdfCarriedBalance")
    val pdfTotalIncome: String get() = values.getValue("pdfTotalIncome")
    val pdfTotalExpense: String get() = values.getValue("pdfTotalExpense")
    val pdfMonthlyNet: String get() = values.getValue("pdfMonthlyNet")
    val pdfClosingBalance: String get() = values.getValue("pdfClosingBalance")
    val pdfProjectSummary: String get() = values.getValue("pdfProjectSummary")
    val pdfGeneralInfo: String get() = values.getValue("pdfGeneralInfo")
    val pdfStartDate: String get() = values.getValue("pdfStartDate")
    val pdfEstimatedEnd: String get() = values.getValue("pdfEstimatedEnd")
    val pdfBuilding: String get() = values.getValue("pdfBuilding")
    val pdfBuildingTemplate: String get() = values.getValue("pdfBuildingTemplate")
    val pdfConstructionProgress: String get() = values.getValue("pdfConstructionProgress")
    val pdfCompletedStages: String get() = values.getValue("pdfCompletedStages")
    val pdfNote: String get() = values.getValue("pdfNote")
    val pdfUnitStatus: String get() = values.getValue("pdfUnitStatus")
    val pdfTotalUnits: String get() = values.getValue("pdfTotalUnits")
    val pdfSold: String get() = values.getValue("pdfSold")
    val pdfReserved: String get() = values.getValue("pdfReserved")
    val pdfAvailable: String get() = values.getValue("pdfAvailable")
    val pdfSalesTotal: String get() = values.getValue("pdfSalesTotal")
    val pdfSoldUnits: String get() = values.getValue("pdfSoldUnits")
    val pdfUnit: String get() = values.getValue("pdfUnit")
    val pdfBuyer: String get() = values.getValue("pdfBuyer")
    val pdfPrice: String get() = values.getValue("pdfPrice")
    val pdfPaid: String get() = values.getValue("pdfPaid")
    val pdfRemaining: String get() = values.getValue("pdfRemaining")
    val pdfBarterSuffix: String get() = values.getValue("pdfBarterSuffix")
    val pdfProjectTransactions: String get() = values.getValue("pdfProjectTransactions")
    val pdfNet: String get() = values.getValue("pdfNet")
    val pdfCraftsmanStatement: String get() = values.getValue("pdfCraftsmanStatement")
    val pdfCraftsmenOverview: String get() = values.getValue("pdfCraftsmenOverview")
    val pdfCraftsmanCount: String get() = values.getValue("pdfCraftsmanCount")
    val pdfTrade: String get() = values.getValue("pdfTrade")
    val pdfDoneAgreed: String get() = values.getValue("pdfDoneAgreed")
    val pdfCurrentStatus: String get() = values.getValue("pdfCurrentStatus")
    val pdfWhenFinished: String get() = values.getValue("pdfWhenFinished")
    val pdfRemainingInvoice: String get() = values.getValue("pdfRemainingInvoice")
    val pdfAgreedWork: String get() = values.getValue("pdfAgreedWork")
    val pdfUnitType: String get() = values.getValue("pdfUnitType")
    val pdfAgreed: String get() = values.getValue("pdfAgreed")
    val pdfDone: String get() = values.getValue("pdfDone")
    val pdfUnitPrice: String get() = values.getValue("pdfUnitPrice")
    val pdfAgreedValue: String get() = values.getValue("pdfAgreedValue")
    val pdfEarned: String get() = values.getValue("pdfEarned")
    val pdfGivenUnits: String get() = values.getValue("pdfGivenUnits")
    val pdfInvoiceStatus: String get() = values.getValue("pdfInvoiceStatus")
    val pdfRequiredInvoice: String get() = values.getValue("pdfRequiredInvoice")
    val pdfIssuedInvoice: String get() = values.getValue("pdfIssuedInvoice")
    val pdfRemainingInvoiceLong: String get() = values.getValue("pdfRemainingInvoiceLong")
    val pdfAccountEntries: String get() = values.getValue("pdfAccountEntries")
    val pdfType: String get() = values.getValue("pdfType")
    val pdfExtraWork: String get() = values.getValue("pdfExtraWork")
    val pdfCashPayment: String get() = values.getValue("pdfCashPayment")
    val pdfIssuedInvoiceEntry: String get() = values.getValue("pdfIssuedInvoiceEntry")
    val pdfEarnedFromWork: String get() = values.getValue("pdfEarnedFromWork")
    val pdfGivenUnitValue: String get() = values.getValue("pdfGivenUnitValue")
    val pdfCashPaidTotal: String get() = values.getValue("pdfCashPaidTotal")
    val pdfCraftsmanOwedText: String get() = values.getValue("pdfCraftsmanOwedText")
    val pdfCraftsmanOwesText: String get() = values.getValue("pdfCraftsmanOwesText")
    val pdfAccountBalanced: String get() = values.getValue("pdfAccountBalanced")
    val pdfStatement: String get() = values.getValue("pdfStatement")
    val pdfBalance: String get() = values.getValue("pdfBalance")
    val pdfPurchase: String get() = values.getValue("pdfPurchase")
    val pdfPayment: String get() = values.getValue("pdfPayment")
    val pdfSale: String get() = values.getValue("pdfSale")
    val pdfCollection: String get() = values.getValue("pdfCollection")
    val pdfTotalPurchases: String get() = values.getValue("pdfTotalPurchases")
    val pdfTotalPayments: String get() = values.getValue("pdfTotalPayments")
    val pdfTotalSales: String get() = values.getValue("pdfTotalSales")
    val pdfTotalCollections: String get() = values.getValue("pdfTotalCollections")
    val pdfCurrentReceivable: String get() = values.getValue("pdfCurrentReceivable")
    val pdfCurrentDebt: String get() = values.getValue("pdfCurrentDebt")
    val dateLabel: String get() = values.getValue("dateLabel")
    val amountFieldLabel: String get() = values.getValue("amountFieldLabel")
    val enterValidAmount: String get() = values.getValue("enterValidAmount")
    val completed: String get() = values.getValue("completed")
    val deleteCraftsmanAction: String get() = values.getValue("deleteCraftsmanAction")
    val updateWork: String get() = values.getValue("updateWork")
    val giveUnitAction: String get() = values.getValue("giveUnitAction")
    val entryAction: String get() = values.getValue("entryAction")
    val givenUnitsSection: String get() = values.getValue("givenUnitsSection")
    val accountEntriesSection: String get() = values.getValue("accountEntriesSection")
    val noEntriesYet: String get() = values.getValue("noEntriesYet")
    val takeBackUnitTitle: String get() = values.getValue("takeBackUnitTitle")
    val takeBackUnitMessage: String get() = values.getValue("takeBackUnitMessage")
    val takeBack: String get() = values.getValue("takeBack")
    val deleteEntryTitle: String get() = values.getValue("deleteEntryTitle")
    val deleteEntryMessage: String get() = values.getValue("deleteEntryMessage")
    val deleteCraftsmanTitle: String get() = values.getValue("deleteCraftsmanTitle")
    val deleteCraftsmanMessage: String get() = values.getValue("deleteCraftsmanMessage")
    val craftsmanOwedTitle: String get() = values.getValue("craftsmanOwedTitle")
    val craftsmanOwesTitle: String get() = values.getValue("craftsmanOwesTitle")
    val accountBalancedTitle: String get() = values.getValue("accountBalancedTitle")
    val agreementTotal: String get() = values.getValue("agreementTotal")
    val doneRemaining: String get() = values.getValue("doneRemaining")
    val earnedFromWorkLine: String get() = values.getValue("earnedFromWorkLine")
    val extraWorkLine: String get() = values.getValue("extraWorkLine")
    val givenUnitValueLine: String get() = values.getValue("givenUnitValueLine")
    val cashPaidLine: String get() = values.getValue("cashPaidLine")
    val whenFinishedLine: String get() = values.getValue("whenFinishedLine")
    val whenFinishedOwed: String get() = values.getValue("whenFinishedOwed")
    val whenFinishedOwes: String get() = values.getValue("whenFinishedOwes")
    val whenFinishedBalanced: String get() = values.getValue("whenFinishedBalanced")
    val givenAsBarter: String get() = values.getValue("givenAsBarter")
    val invoiceCardTitle: String get() = values.getValue("invoiceCardTitle")
    val invoiceRemainingShort: String get() = values.getValue("invoiceRemainingShort")
    val invoiceIssuedOf: String get() = values.getValue("invoiceIssuedOf")
    val invoiceCardHint: String get() = values.getValue("invoiceCardHint")
    val invoiceDialogTitle: String get() = values.getValue("invoiceDialogTitle")
    val invoiceDialogHint: String get() = values.getValue("invoiceDialogHint")
    val updateWorkTitle: String get() = values.getValue("updateWorkTitle")
    val updateWorkHint: String get() = values.getValue("updateWorkHint")
    val workTypeAgreedLabel: String get() = values.getValue("workTypeAgreedLabel")
    val entryTypeCash: String get() = values.getValue("entryTypeCash")
    val entryTypeExtra: String get() = values.getValue("entryTypeExtra")
    val entryTypeInvoice: String get() = values.getValue("entryTypeInvoice")
    val entryCashTitle: String get() = values.getValue("entryCashTitle")
    val entryExtraTitle: String get() = values.getValue("entryExtraTitle")
    val entryInvoiceTitle: String get() = values.getValue("entryInvoiceTitle")
    val descCashHint: String get() = values.getValue("descCashHint")
    val descExtraHint: String get() = values.getValue("descExtraHint")
    val descInvoiceHint: String get() = values.getValue("descInvoiceHint")
    val hintCashPayment: String get() = values.getValue("hintCashPayment")
    val hintExtraWork: String get() = values.getValue("hintExtraWork")
    val hintInvoice: String get() = values.getValue("hintInvoice")
    val giveUnitTitle: String get() = values.getValue("giveUnitTitle")
    val giveUnitHint: String get() = values.getValue("giveUnitHint")
    val unitValueLabel: String get() = values.getValue("unitValueLabel")
    val give: String get() = values.getValue("give")
    val selectUnitError: String get() = values.getValue("selectUnitError")
    val enterUnitValueError: String get() = values.getValue("enterUnitValueError")
    val unitLabel: String get() = values.getValue("unitLabel")
    val importing: String get() = values.getValue("importing")
    val importDoneTitle: String get() = values.getValue("importDoneTitle")
    val importDoneMessage: String get() = values.getValue("importDoneMessage")
    val importFailedTitle: String get() = values.getValue("importFailedTitle")
    val importPreviewTitle: String get() = values.getValue("importPreviewTitle")
    val importIncomeRows: String get() = values.getValue("importIncomeRows")
    val importExpenseRows: String get() = values.getValue("importExpenseRows")
    val importHouseRows: String get() = values.getValue("importHouseRows")
    val importSkippedSales: String get() = values.getValue("importSkippedSales")
    val importRowCount: String get() = values.getValue("importRowCount")
    val importTargetProject: String get() = values.getValue("importTargetProject")
    val importUnknownProjects: String get() = values.getValue("importUnknownProjects")
    val importSkippedRows: String get() = values.getValue("importSkippedRows")
    val importAction: String get() = values.getValue("importAction")
    val importNoRows: String get() = values.getValue("importNoRows")
    val importSalesSkippedNote: String get() = values.getValue("importSalesSkippedNote")
    val importFailedMessage: String get() = values.getValue("importFailedMessage")
    val unknownError: String get() = values.getValue("unknownError")
    val projectNameLabel: String get() = values.getValue("projectNameLabel")
    val locationLabel: String get() = values.getValue("locationLabel")
    val startLabel: String get() = values.getValue("startLabel")
    val estimatedEndLabel: String get() = values.getValue("estimatedEndLabel")
    val floorCountLabel: String get() = values.getValue("floorCountLabel")
    val unitsPerFloorLabel: String get() = values.getValue("unitsPerFloorLabel")
    val coverColorLabel: String get() = values.getValue("coverColorLabel")
    val createProject: String get() = values.getValue("createProject")
    val errorProjectName: String get() = values.getValue("errorProjectName")
    val errorFloors: String get() = values.getValue("errorFloors")
    val errorUnitsPerFloor: String get() = values.getValue("errorUnitsPerFloor")
    val newTransaction: String get() = values.getValue("newTransaction")
    val editTransaction: String get() = values.getValue("editTransaction")
    val incomeType: String get() = values.getValue("incomeType")
    val expenseCategory: String get() = values.getValue("expenseCategory")
    val descriptionOptional: String get() = values.getValue("descriptionOptional")
    val soldUnitLabel: String get() = values.getValue("soldUnitLabel")
    val errorSelectIncomeType: String get() = values.getValue("errorSelectIncomeType")
    val errorSelectExpenseCategory: String get() = values.getValue("errorSelectExpenseCategory")
    val errorSelectProjectForSale: String get() = values.getValue("errorSelectProjectForSale")
    val errorSelectSoldUnit: String get() = values.getValue("errorSelectSoldUnit")
    val errorEnterDescription: String get() = values.getValue("errorEnterDescription")
    val noUnitsAvailable: String get() = values.getValue("noUnitsAvailable")
    val noUnitsDefined: String get() = values.getValue("noUnitsDefined")
    val saleMarksUnit: String get() = values.getValue("saleMarksUnit")
    val deleteProjectAction: String get() = values.getValue("deleteProjectAction")
    val buildingLabel: String get() = values.getValue("buildingLabel")
    val buildingValue: String get() = values.getValue("buildingValue")
    val constructionStatus: String get() = values.getValue("constructionStatus")
    val progressLabel: String get() = values.getValue("progressLabel")
    val showStages: String get() = values.getValue("showStages")
    val completedCheck: String get() = values.getValue("completedCheck")
    val completedParty: String get() = values.getValue("completedParty")
    val buyersSection: String get() = values.getValue("buyersSection")
    val projectTransactionsSection: String get() = values.getValue("projectTransactionsSection")
    val noProjectTransactions: String get() = values.getValue("noProjectTransactions")
    val deleteProjectTitle: String get() = values.getValue("deleteProjectTitle")
    val deleteProjectMessage: String get() = values.getValue("deleteProjectMessage")
    val barterUnitSubtitle: String get() = values.getValue("barterUnitSubtitle")
    val floorShort: String get() = values.getValue("floorShort")
    val statusSold: String get() = values.getValue("statusSold")
    val statusReserved: String get() = values.getValue("statusReserved")
    val statusAvailable: String get() = values.getValue("statusAvailable")
    val barterShort: String get() = values.getValue("barterShort")
    val salePriceLabel: String get() = values.getValue("salePriceLabel")
    val saleDateLabel: String get() = values.getValue("saleDateLabel")
    val buyerNameLabel: String get() = values.getValue("buyerNameLabel")
    val buyerPhoneLabel: String get() = values.getValue("buyerPhoneLabel")
    val paymentsReceived: String get() = values.getValue("paymentsReceived")
    val addPayment: String get() = values.getValue("addPayment")
    val deletePayment: String get() = values.getValue("deletePayment")
    val remainingDebt: String get() = values.getValue("remainingDebt")
    val fullyPaid: String get() = values.getValue("fullyPaid")
    val paidAmount: String get() = values.getValue("paidAmount")
    val errorSalePrice: String get() = values.getValue("errorSalePrice")
    val noBuyerGiven: String get() = values.getValue("noBuyerGiven")
    val barterInfo: String get() = values.getValue("barterInfo")
    val saleSummary: String get() = values.getValue("saleSummary")
    val deleteAccountAction: String get() = values.getValue("deleteAccountAction")
    val printPdf: String get() = values.getValue("printPdf")
    val materialPurchase: String get() = values.getValue("materialPurchase")
    val materialSale: String get() = values.getValue("materialSale")
    val paymentOrCarry: String get() = values.getValue("paymentOrCarry")
    val collectionOrCarry: String get() = values.getValue("collectionOrCarry")
    val noEntriesSupplier: String get() = values.getValue("noEntriesSupplier")
    val noEntriesCustomer: String get() = values.getValue("noEntriesCustomer")
    val purchasePrefix: String get() = values.getValue("purchasePrefix")
    val salePrefix: String get() = values.getValue("salePrefix")
    val deleteRecordTitle: String get() = values.getValue("deleteRecordTitle")
    val thisRecord: String get() = values.getValue("thisRecord")
    val deleteRecordMessage: String get() = values.getValue("deleteRecordMessage")
    val deleteCustomerTitle: String get() = values.getValue("deleteCustomerTitle")
    val deleteSupplierTitle: String get() = values.getValue("deleteSupplierTitle")
    val deleteAccountMessage: String get() = values.getValue("deleteAccountMessage")
    val materialSaleEntry: String get() = values.getValue("materialSaleEntry")
    val materialPurchaseEntry: String get() = values.getValue("materialPurchaseEntry")
    val makePayment: String get() = values.getValue("makePayment")
    val addCarryReceivable: String get() = values.getValue("addCarryReceivable")
    val addCarryDebt: String get() = values.getValue("addCarryDebt")
    val carryReceivable: String get() = values.getValue("carryReceivable")
    val carryDebt: String get() = values.getValue("carryDebt")
    val descPaymentHint: String get() = values.getValue("descPaymentHint")
    val descCarryHint: String get() = values.getValue("descCarryHint")
    val hintCarryReceivable: String get() = values.getValue("hintCarryReceivable")
    val hintCarryDebt: String get() = values.getValue("hintCarryDebt")
    val editPurchase: String get() = values.getValue("editPurchase")
    val editSale: String get() = values.getValue("editSale")
    val itemNameLabel: String get() = values.getValue("itemNameLabel")
    val deleteRow: String get() = values.getValue("deleteRow")
    val rowTotal: String get() = values.getValue("rowTotal")
    val errorAtLeastOneItem: String get() = values.getValue("errorAtLeastOneItem")
    val errorIncompleteRow: String get() = values.getValue("errorIncompleteRow")
    val errorEnterReceiver: String get() = values.getValue("errorEnterReceiver")
    val kurusLabel: String get() = values.getValue("kurusLabel")
    val blockWord: String get() = values.getValue("blockWord")
    val addToDailyLedger: String get() = values.getValue("addToDailyLedger")
    val addToDailyLedgerHintOn: String get() = values.getValue("addToDailyLedgerHintOn")
    val addToDailyLedgerHintOff: String get() = values.getValue("addToDailyLedgerHintOff")
    val errorProjectRequiredOffLedger: String get() = values.getValue("errorProjectRequiredOffLedger")
    val projectOnlyBadge: String get() = values.getValue("projectOnlyBadge")
    val blocksSection: String get() = values.getValue("blocksSection")
    val blocksHint: String get() = values.getValue("blocksHint")
    val addBlock: String get() = values.getValue("addBlock")
    val removeBlock: String get() = values.getValue("removeBlock")
    val blockNameLabel: String get() = values.getValue("blockNameLabel")
    val errorBlockLayout: String get() = values.getValue("errorBlockLayout")
    val blockLayoutSummary: String get() = values.getValue("blockLayoutSummary")
    val pdfBlocks: String get() = values.getValue("pdfBlocks")
    val buildingStatus: String get() = values.getValue("buildingStatus")
    val blockSummary: String get() = values.getValue("blockSummary")
    val pdfBlock: String get() = values.getValue("pdfBlock")
}

/** "Daire 7" / "A Blok • Daire 7" — localized, block-aware. */
fun unitDisplayLabel(block: String, number: Int): String {
    val s = Localization.strings
    val unit = "${s.unitLabel} $number"
    return if (block.isBlank()) unit else "$block ${s.blockWord} • $unit"
}

/** Category names are stored in Turkish; unknown/custom ones pass through. */
fun localizedCategory(stored: String): String =
    Localization.strings.categoryLabels[stored] ?: stored

/** Template helper for call sites outside [Strings]. */
fun String.fillTemplate(vararg values: Pair<String, String>): String =
    values.fold(this) { acc, (key, value) -> acc.replace("{$key}", value) }

val AppLanguage.strings: Strings
    get() = when (this) {
        AppLanguage.TURKISH -> TrStrings
        AppLanguage.ENGLISH -> EnStrings
        AppLanguage.SPANISH -> EsStrings
    }

/**
 * App-wide language holder. Initialized at startup from the stored user
 * preference, falling back to the device language (unsupported → English).
 */
object Localization {

    private const val STORAGE_KEY = "app_language"

    private var storage: AppStorage? = null

    private val _language = MutableStateFlow(AppLanguage.TURKISH)
    val language: StateFlow<AppLanguage> = _language.asStateFlow()

    /** For non-compose code (view models, exports, date formatting). */
    val strings: Strings get() = _language.value.strings

    fun init(storage: AppStorage) {
        this.storage = storage
        _language.value = AppLanguage.fromCode(storage.getString(STORAGE_KEY))
            ?: AppLanguage.fromCode(systemLanguageCode())
            ?: AppLanguage.ENGLISH
    }

    fun setLanguage(language: AppLanguage) {
        _language.value = language
        storage?.putString(STORAGE_KEY, language.code)
    }
}

/** Compose access; provided at the App root, updates on language change. */
val LocalStrings = staticCompositionLocalOf { TrStrings }
